import { useMemo, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { candidatosApi } from '@/lib/api/candidatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { Info } from '@/design-system/Info';

/**
 * `histSec()` (proto:1408-1420) — seção que, no protótipo, absorveu o
 * conteúdo da tela `eleg-cand` ("Séries históricas") dentro da visão geral
 * eleitoral.
 *
 * Honestidade de dado: o protótipo desenha, por eleição, votos por bairro
 * (hbar), um comparativo por bairro entre ciclos e um ranking "onde cresceu
 * · onde caiu" por bairro. Essa granularidade não existe nos dados reais —
 * a apuração do TSE (`eleg.resultado_zona`) é por ZONA ELEITORAL, e não há
 * mapeamento zona↔bairro no schema (mesma lacuna documentada em
 * `MatrizForcaMomento.tsx`). Por isso as seções abaixo usam zona eleitoral
 * no lugar de bairro — dado real, granularidade diferente da do protótipo —
 * e cada card diz isso explicitamente.
 *
 * Também ficam de fora, por não terem endpoint hoje (ver relatório da Task
 * 4.3): percentual sobre votos válidos do pleito, posição/colocação final,
 * brancos/nulos e abstenção do pleito, número de seções apuradas e gasto de
 * campanha declarado. Nenhum desses é estimado.
 */
const ROTULO_RESULTADO: Record<string, string> = {
  eleito: 'Eleita',
  nao_eleito: 'Não eleita',
  suplente: 'Suplente',
};

const CORES_CICLO = ['var(--warn)', 'var(--accent)', 'var(--eleg)', 'var(--good)', 'var(--crit)'];

export function HistoricoSecao({ candidatoId }: { candidatoId: string }) {
  const token = useAuthStore((s) => s.token) || '';
  const [comparativo, setComparativo] = useState(true);

  const { data: candidato, isLoading } = useQuery({
    queryKey: ['candidatos', candidatoId],
    queryFn: () => candidatosApi.get(candidatoId, token),
    enabled: !!token && !!candidatoId,
  });

  const candidaturas = useMemo(
    () => [...(candidato?.candidaturas ?? [])].sort((a, b) => a.ciclo - b.ciclo),
    [candidato],
  );

  const todosOsAnos = candidaturas.map((c) => c.ciclo);
  const [anosSelecionados, setAnosSelecionados] = useState<number[]>([]);
  const anos = anosSelecionados.length ? anosSelecionados : todosOsAnos;

  const alternarAno = (ano: number) => {
    setAnosSelecionados((atual) => {
      const base = atual.length ? atual : todosOsAnos;
      const i = base.indexOf(ano);
      if (i > -1) {
        if (base.length > 1) return base.filter((a) => a !== ano);
        return base;
      }
      return [...base, ano].sort((a, b) => a - b);
    });
  };

  const selecionadas = candidaturas.filter((c) => anos.includes(c.ciclo));

  // Cor fixa por ciclo, calculada uma única vez sobre TODAS as candidaturas
  // (não sobre `selecionadas`): assim a cor de um ano não muda conforme o
  // subconjunto de anos escolhido no filtro, e a borda do card bate com o
  // cabeçalho da tabela comparativa mesmo quando só parte dos anos está
  // selecionada.
  const corPorCiclo = useMemo(() => {
    const mapa = new Map<number, string>();
    candidaturas.forEach((c, i) => mapa.set(c.ciclo, CORES_CICLO[i % CORES_CICLO.length]!));
    return mapa;
  }, [candidaturas]);

  // Votos por zona de cada ciclo selecionado — mesma fonte que `resultadosPorZona`
  // usa em `ElegCandPage`, aqui restrita ao candidato desta seção.
  const consultasZona = useQueries({
    queries: selecionadas.map((c) => ({
      queryKey: ['resultados-zona', c.ciclo],
      queryFn: () => candidatosApi.resultadosPorZona(c.ciclo, token),
      enabled: !!token,
    })),
  });

  const zonasPorCiclo = new Map<number, Array<{ zona: string; votos: number }>>();
  selecionadas.forEach((c, i) => {
    const resultados = consultasZona[i]?.data ?? [];
    const doCandidato = resultados.filter((r) => r.candidato_id === candidatoId);
    const porZona = new Map<string, number>();
    doCandidato.forEach((r) => porZona.set(r.zona, (porZona.get(r.zona) || 0) + r.votos));
    zonasPorCiclo.set(
      c.ciclo,
      [...porZona.entries()].map(([zona, votos]) => ({ zona, votos })).sort((a, b) => b.votos - a.votos),
    );
  });

  if (isLoading) {
    return (
      <div id="hist-sec" className="card" style={{ borderColor: 'var(--eleg)' }}>
        <div className="empty">Carregando histórico eleitoral...</div>
      </div>
    );
  }

  if (candidaturas.length === 0) {
    return (
      <div id="hist-sec" className="card" style={{ borderColor: 'var(--eleg)' }}>
        <h2 style={{ color: 'var(--eleg)' }}>Histórico eleitoral da candidata</h2>
        <div className="empty">Nenhuma candidatura carregada para este candidato.</div>
      </div>
    );
  }

  const maxVotosGeral = Math.max(1, ...candidaturas.map((c) => c.votos));

  return (
    <>
      <div id="hist-sec" className="card" style={{ borderColor: 'var(--eleg)' }}>
        <h2 style={{ color: 'var(--eleg)' }}>
          Histórico eleitoral da candidata · últimas {candidaturas.length} eleições disputadas
        </h2>
        <p style={{ fontSize: 13, color: 'var(--ink2)', margin: '0 0 10px' }}>
          Dados oficiais de cada disputa (TSE): cargo, número, partido, votos e resultado.
        </p>
        <div className="filters">
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Eleições:</span>
          {candidaturas.map((c) => (
            <span
              key={c.ciclo}
              className={`chip neutral chipbtn ${anos.includes(c.ciclo) ? 'on' : ''}`}
              onClick={() => alternarAno(c.ciclo)}
            >
              {c.ciclo} · {c.cargo}
            </span>
          ))}
          <span
            className={`chip neutral chipbtn ${anos.length === todosOsAnos.length ? 'on' : ''}`}
            onClick={() => setAnosSelecionados(todosOsAnos)}
          >
            Todas
          </span>
          <span style={{ flex: 1 }} />
          <label className={`tog ${comparativo ? 'on' : ''}`} onClick={() => setComparativo((v) => !v)}>
            <i />
            Comparativo
          </label>
        </div>
      </div>

      <div className="grid g3">
        {selecionadas.map((c) => {
          const cor = corPorCiclo.get(c.ciclo)!;
          const zonas = zonasPorCiclo.get(c.ciclo) ?? [];
          const maxZona = Math.max(1, ...zonas.map((z) => z.votos));
          return (
            <div key={c.ciclo} className="card" style={{ borderTop: `3px solid ${cor}` }}>
              <h2>
                {c.ciclo} · {c.cargo}
                <span
                  className={`chip ${c.resultado === 'eleito' ? 'good' : c.resultado === 'suplente' ? 'warn' : 'neutral'}`}
                  style={{ marginLeft: 'auto' }}
                >
                  {ROTULO_RESULTADO[c.resultado] || c.resultado}
                </span>
              </h2>
              <div className="kpi" style={{ marginBottom: 8 }}>
                <div className="v num" style={{ fontSize: 30 }}>
                  {c.votos.toLocaleString('pt-BR')} <span style={{ fontSize: 13, color: 'var(--ink3)' }}>votos</span>
                </div>
              </div>
              <dl className="dl">
                <dt>Nº de urna</dt>
                <dd className="mono">{c.numero_urna}</dd>
                <dt>Partido</dt>
                <dd>{c.partido}</dd>
                <dt>Zonas apuradas</dt>
                <dd className="num">{c.zonas}</dd>
                <dt>Votos apurados por zona</dt>
                <dd className="num">{c.votos_apurados.toLocaleString('pt-BR')}</dd>
              </dl>
              <h3 style={{ margin: '12px 0 6px' }}>
                Votos por zona eleitoral{' '}
                <Info titulo="Por que a granularidade é a zona, e não o bairro">
                  Não há mapeamento entre zona eleitoral e bairro na base, então a apuração não se
                  abre por território.
                </Info>
              </h3>
              {zonas.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--ink3)' }}>
                  Sem resultados por zona carregados para {c.ciclo}.
                </p>
              ) : (
                <div className="rank">
                  {zonas.map((z) => (
                    <div key={z.zona}>
                      <span style={{ width: 70 }}>Zona {z.zona}</span>
                      <span className="b">
                        <i style={{ width: `${(z.votos / maxZona) * 100}%`, background: cor }} />
                      </span>
                      <b className="num" style={{ width: 48, textAlign: 'right' }}>
                        {z.votos.toLocaleString('pt-BR')}
                      </b>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '6px 0 0' }}>
                Fonte: TSE resultado_zona {c.ciclo} · agregado por zona eleitoral.
              </p>
            </div>
          );
        })}
      </div>

      {comparativo && selecionadas.length > 1 && (
        <div className="grid g21">
          <div className="card">
            <h2>
              Comparativo por zona eleitoral · {selecionadas.map((c) => c.ciclo).join(' × ')}
            </h2>
            <ComparativoZonas
              selecionadas={selecionadas}
              zonasPorCiclo={zonasPorCiclo}
              corPorCiclo={corPorCiclo}
            />
          </div>
          <div className="card">
            <h2>Evolução geral</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 140, marginTop: 12 }}>
              {selecionadas.map((c) => {
                const h = (c.votos / maxVotosGeral) * 110;
                const cor = corPorCiclo.get(c.ciclo)!;
                return (
                  <div key={c.ciclo} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 11 }}>{c.votos.toLocaleString('pt-BR')}</div>
                    <div
                      style={{
                        height: Math.max(2, h),
                        background: cor,
                        borderRadius: '4px 4px 0 0',
                      }}
                    />
                    <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 4 }}>{c.ciclo}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ComparativoZonas({
  selecionadas,
  zonasPorCiclo,
  corPorCiclo,
}: {
  selecionadas: Array<{ ciclo: number }>;
  zonasPorCiclo: Map<number, Array<{ zona: string; votos: number }>>;
  corPorCiclo: Map<number, string>;
}) {
  const todasZonas = [...new Set(selecionadas.flatMap((c) => (zonasPorCiclo.get(c.ciclo) ?? []).map((z) => z.zona)))].sort();

  if (todasZonas.length === 0) {
    return <div className="empty">Sem resultados por zona carregados para os ciclos selecionados.</div>;
  }

  const votosDe = (ciclo: number, zona: string) =>
    (zonasPorCiclo.get(ciclo) ?? []).find((z) => z.zona === zona)?.votos ?? 0;

  return (
    <div className="tw">
      <table>
        <thead>
          <tr>
            <th>Zona</th>
            {selecionadas.map((c) => (
              <th key={c.ciclo} style={{ color: corPorCiclo.get(c.ciclo) }}>
                {c.ciclo}
              </th>
            ))}
            <th>Variação {selecionadas[0]!.ciclo}→{selecionadas[selecionadas.length - 1]!.ciclo}</th>
          </tr>
        </thead>
        <tbody>
          {todasZonas.map((zona) => {
            const vs = selecionadas.map((c) => votosDe(c.ciclo, zona));
            const d = vs[vs.length - 1]! - vs[0]!;
            return (
              <tr key={zona} className="row">
                <td>
                  <b>Zona {zona}</b>
                </td>
                {vs.map((v, i) => (
                  <td key={selecionadas[i]!.ciclo} className="num">
                    {v.toLocaleString('pt-BR')}
                  </td>
                ))}
                <td className="num" style={{ color: d >= 0 ? 'var(--good)' : 'var(--crit)' }}>
                  {d >= 0 ? '+' : ''}
                  {d.toLocaleString('pt-BR')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
